import { useCallback, useMemo, useRef } from 'react';
import { useGenericEventsLogger } from '@wix/data-tools-generic-events';
import type { Entity } from '@wix/data-tools-generic-events';
import type {
  bmModalActionSrc172Evid572Params,
  bmPageClickSrc172Evid522Params,
  bmPageLoadSrc172Evid501Params,
} from '@wix/bi-logger-scheduling-assistant-data/v2/types';
import {
  logModalActionBi as logModalActionBiV172,
  logPageClickBi as logPageClickBiV172,
  logPageLoadBi as logPageLoadBiV172,
  shouldLogBi,
} from './bi-logger';

// The entity FQDNs and the pure field helpers live in `lib/bi-fields.ts` — see the note there for why
// they are not in this module. Re-exported so every existing import of them keeps working.
export { ENTITY_TYPE, toAdditionalFields, toKebab } from './bi-fields';
import { ENTITY_TYPE, toAdditionalFields, toKebab } from './bi-fields';

// Options for the click/modal loggers. `entity` lets a call site attach the
// business entity it acted on (id + type) — used for the generic events'
// `entity` field, from which the library derives root `entities`. `componentName`
// overrides the default (kebab of the src:172 button_name) when the click's identity
// is dynamic — e.g. the specific pulse or For-You item the user clicked — so the
// generic componentName carries that name while the src:172 button_name stays stable.
// `additionalFields` carries flat string context the src:172 params have no registered
// field for — the country a user picked, the choice text they tapped, the plan behind an
// Upgrade. Without it a click can only report the fixed src:172 field set, which is why
// the BI QA round found several clicks arriving with no way to tell WHAT was clicked.
// Merged UNDER the derived src:172 fields, so it can never shadow `page_name` & co.
type GenericBiOpts = {
  entity?: Entity;
  componentName?: string;
  additionalFields?: Record<string, string | undefined>;
};

// Options for the component-level load loggers (loadStart 198:4 / loadFinish 198:5).
// `entity` attaches the business entity the region loaded (id + type);
// `additionalFields` carries flat string context (e.g. `source`, item counts).
export type LoadBiOpts = {
  entity?: Entity;
  additionalFields?: Record<string, string | undefined>;
};

// The generic `entity` for a click. An explicit `opts.entity` wins; otherwise
// derive it from the standard id fields the src:172 params already carry
// (`task_id`, `conversation_id`) so those call sites need no change.
const deriveClickEntity = (
  params: bmPageClickSrc172Evid522Params,
  opts?: GenericBiOpts,
): Entity | undefined => {
  if (opts?.entity?.id) return opts.entity;
  if (params.task_id) return { id: params.task_id, type: ENTITY_TYPE.task };
  if (params.conversation_id) {
    return { id: String(params.conversation_id), type: ENTITY_TYPE.conversation };
  }
  return undefined;
};


/**
 * The `additionalFields` a click reports: the caller's own payload, with the derived src:172 fields
 * layered ON TOP so a caller can never shadow the event's own identity (a stray `page_name` in a
 * payload must not be able to rewrite which page the click happened on).
 *
 * Exported and pure so the merge and its precedence are directly testable — this suite runs in a
 * node environment with no React renderer, and the ordering here is the whole guarantee.
 */
export const clickAdditionalFields = (
  params: bmPageClickSrc172Evid522Params,
  extra?: Record<string, string | undefined>,
): Record<string, string> =>
  toAdditionalFields({
    ...extra,
    page_name: params.page_name,
    page_version: params.page_version,
    origin: params.origin,
    location: params.location,
    action: params.action,
    answer: params.answer,
  });

/**
 * Dual-fires the existing Orion src:172 BI events **and** their generic (src:198)
 * equivalents, reusing the same field values. src:172 stays the source of truth;
 * the generic events run alongside for the platform-generic BI stream.
 *
 * Mapping (existing → generic):
 * - `logPageClickBi`  (pageClick 522)   → `clickCta`   (198:10)
 * - `logModalActionBi` (modalAction 572) → `clickCta`   (198:10)
 * - `logPageLoadBi`   (pageLoad 501)     → `loadFinish` (198:5)
 *
 * The load pair (198:4/5) has no src:172 equivalent and fires generic-only, as does `itemSeen` (198:6) —
 * for which the entry point is `useItemSeenBi` (`lib/use-item-seen-bi.ts`), not a logger here, because an
 * impression is a viewport fact rather than something a handler decides.
 *
 * `pageName` / `componentRoute` come from `PageContextProvider` (wired in _app);
 * the original per-call field values ride along in `additionalFields` so nothing
 * is lost. Gated by `shouldLogBi()` to match the src:172 behavior — logs on every
 * surface, including the Symphony root host.
 */
/**
 * @param componentParentName Optional route segment to append for THIS hook only.
 *   Use it in a component that fires its own clicks AND renders no wrapping
 *   `GenericEventsRouteProvider` above itself (a self-contained page/section) —
 *   e.g. `useGenericBi('notifications')`. It does NOT propagate to child
 *   components; for shared parent context, wrap children with
 *   `GenericEventsRouteProvider` instead.
 */
export const useGenericBi = (componentParentName?: string) => {
  const logger = useGenericEventsLogger(
    componentParentName ? { componentParentName } : undefined,
  );

  // Every logger below is `useCallback`-stable, and reads the CURRENT logger through this ref rather
  // than closing over it. That is the whole point: these functions land in consumers' `useCallback`
  // dependency arrays, and `useGenericEventsLogger` gives a fresh identity per render — so depending
  // on it directly made every returned function change on every render. A consumer that fed one into
  // a fetching `useEffect` then re-fetched on every render forever: `useAppsController` did exactly
  // that and hammered /api/connectors/status at ~33 req/s, starving the voice call's WebSocket
  // handshake (30s to connect) until the browser reported ERR_NETWORK_CHANGED.
  const loggerRef = useRef(logger);
  loggerRef.current = logger;

  const logPageClickBi = useCallback((params: bmPageClickSrc172Evid522Params, opts?: GenericBiOpts) => {
    const logger = loggerRef.current;
    logPageClickBiV172(params);
    if (!shouldLogBi()) return;
    const entity = deriveClickEntity(params, opts);
    // componentName is the specific action name — an explicit opts.componentName
    // (e.g. the clicked pulse / For-You item) wins over the src:172 button_name;
    // componentRoute + componentParentName are filled automatically from the
    // GenericEventsRouteProvider hierarchy in the component tree.
    logger.clickCta({
      componentName: toKebab(opts?.componentName || params.button_name) || 'unknown',
      ...(entity ? { entity } : {}),
      additionalFields: clickAdditionalFields(params, opts?.additionalFields),
    });
  }, []);

  const logModalActionBi = useCallback((params: bmModalActionSrc172Evid572Params, opts?: GenericBiOpts) => {
    const logger = loggerRef.current;
    logModalActionBiV172(params);
    if (!shouldLogBi()) return;
    const entity = opts?.entity?.id
      ? opts.entity
      : params.task_id
      ? { id: params.task_id, type: ENTITY_TYPE.task }
      : undefined;
    logger.clickCta({
      componentName: toKebab(params.modal_name) || 'modal',
      ...(entity ? { entity } : {}),
      additionalFields: toAdditionalFields({
        page_name: params.page_name,
        page_version: params.page_version,
        origin: params.origin,
        action: params.action,
        answer: params.answer,
        modal_page_name: params.modal_page_name,
      }),
    });
  }, []);

  const logPageLoadBi = useCallback((
    params: bmPageLoadSrc172Evid501Params,
    opts?: {
      additionalFields?: Record<string, string>;
      isSuccessful?: boolean;
      loadingTimeMs?: number;
      /**
       * The generic event's componentName — the canonical page name from
       * `bi-page-name.ts`, passed by `usePageLoadBi`. Without it we fall back to the
       * src:172 `page_name`, which is a different naming scheme (`settings_models_page`
       * where the shared vocabulary says `agent_models`) and so wouldn't join to native.
       */
      componentName?: string;
    },
  ) => {
    const logger = loggerRef.current;
    logPageLoadBiV172(params);
    if (!shouldLogBi()) return;
    // Page load has no native generic event; map to loadFinish (198:5) as the
    // page "finishing loading". `isSuccessful`/`loadingTimeMs` reflect the real
    // load outcome when the caller can observe it (via `usePageLoadBi`'s query
    // state); they default to a successful zero-duration beacon otherwise.
    // page_name is intentionally NOT duplicated into additionalFields — it is
    // already carried by the event's componentName/pageName. `opts.additionalFields`
    // lets a page attach load-state context (e.g. which connectors came back connected).
    logger.loadFinish({
      componentName: toKebab(opts?.componentName || params.page_name) || 'page',
      loadingTimeMs: opts?.loadingTimeMs ?? 0,
      isSuccessful: opts?.isSuccessful ?? true,
      additionalFields: toAdditionalFields({
        page_version: params.page_version,
        origin: params.origin,
        prefilled_value: params.prefilled_value,
        referral_url: params.referral_url ? String(params.referral_url) : undefined,
        state: params.state,
        ...opts?.additionalFields,
      }),
    });
  }, []);

  // Component-level async-region tracking. These are pure src:198 (there is no
  // src:172 equivalent for a component starting/finishing a load — the legacy 501
  // is a whole-page view, not a data region), matching the native apps where
  // loadStart (198:4) / loadFinish (198:5) fire on screens, modals, sheets and
  // data-regions that kick off a genuine async fetch. Pair them via `useLoadBi`.
  const logLoadStart = useCallback((componentName: string, opts?: LoadBiOpts) => {
    if (!shouldLogBi()) return;
    loggerRef.current.loadStart({
      componentName: toKebab(componentName) || 'component',
      ...(opts?.entity ? { entity: opts.entity } : {}),
      ...(opts?.additionalFields
        ? { additionalFields: toAdditionalFields(opts.additionalFields) }
        : {}),
    });
  }, []);

  const logLoadFinish = useCallback((
    componentName: string,
    result: { isSuccessful: boolean; loadingTimeMs?: number },
    opts?: LoadBiOpts,
  ) => {
    if (!shouldLogBi()) return;
    loggerRef.current.loadFinish({
      componentName: toKebab(componentName) || 'component',
      loadingTimeMs: result.loadingTimeMs ?? 0,
      isSuccessful: result.isSuccessful,
      ...(opts?.entity ? { entity: opts.entity } : {}),
      ...(opts?.additionalFields
        ? { additionalFields: toAdditionalFields(opts.additionalFields) }
        : {}),
    });
  }, []);

  // The OBJECT has to be stable too, not just its members — a consumer that destructures is safe
  // either way, but one that depends on the hook's return value directly would churn on a fresh literal.
  return useMemo(
    () => ({ logPageClickBi, logModalActionBi, logPageLoadBi, logLoadStart, logLoadFinish }),
    [logPageClickBi, logModalActionBi, logPageLoadBi, logLoadStart, logLoadFinish],
  );
};
